import React, { ReactNode } from 'react';

import type { PermissionContextVariables, PermissionKey } from '@/hooks/permissions/usePermissions';
import { useAllPermissions, useAnyPermissions } from '@/hooks/permissions/usePermissions';

interface PermissionGuardProps {
  children: ReactNode;
  all?: PermissionKey[];
  any?: PermissionKey[];
  variables?: PermissionContextVariables;
  errorFallback?: ReactNode;
  accessDeniedFallback?: ReactNode;
  loadingFallback?: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  all,
  any,
  variables,
  errorFallback,
  accessDeniedFallback,
  loadingFallback
}) => {
  const {
    value: allGranted,
    isLoading: isLoadingAll,
    errors: allErrors,
    isAccessDenied: isAccessDeniedAll
  } = useAllPermissions({
    permissions: all ?? [],
    variables: variables ?? {}
  });
  const {
    value: anyGranted,
    isLoading: isLoadingAny,
    errors: anyErrors,
    isAccessDenied: isAccessDeniedAny
  } = useAnyPermissions({
    permissions: any ?? [],
    variables: variables ?? {}
  });

  const isLoading = isLoadingAll || isLoadingAny;
  const isAllMode = all && all.length > 0;
  const hasPermission = isAllMode ? allGranted : anyGranted;
  const errors = isAllMode ? allErrors : anyErrors;
  const isAccessDenied = isAllMode ? isAccessDeniedAll : isAccessDeniedAny;
  const hasErrors = errors.length > 0;

  if (!hasPermission || isAccessDenied) {
    return accessDeniedFallback ?? null;
  }

  if (hasErrors) {
    return errorFallback ?? null;
  }

  if (isLoading) {
    return loadingFallback ?? null;
  }

  return <>{children}</>;
};
